import type { MessageKey } from "../i18n/keys";
/** Pure helpers of the permit plan set screens (permits/: the workspace and its summary); tested in permits.test.ts.
 * Text comes from the `permit` catalogs: helpers that word something take the caller's `t` (useT()). */

/** The caller's translator (useT() in a component); a Lux amount is passed as `lux` in Lux (hundredths / 100). */
export type Tr = (key: MessageKey, values?: Record<string, string | number | Date>) => string;

/** A check is a problem (a real design error; it never blocks generation) or passed; notes are "info". */
export type CheckStatus = "pass" | "problem" | "info";
export type Country = "US" | "CA" | "MX" | "GB" | "AU";

export interface CheckLike {
  id: string;
  status: CheckStatus;
  /** A gap a final plan set must not have (Generate with open items; lib/permitEquipment.ts). */
  blocking?: boolean | null;
}

export const COUNTRIES: { value: Country; label: MessageKey }[] = [
  { value: "US", label: "permit.country.us" },
  { value: "CA", label: "permit.country.ca" },
  { value: "MX", label: "permit.country.mx" },
  { value: "GB", label: "permit.country.gb" },
  { value: "AU", label: "permit.country.au" },
];

/** The Equipment step's country choices. While no country is known (a company outside the plan-set countries whose
 * project names none), "Choose the country" comes first: the select used to show United States without it being
 * chosen, and picking United States then changed nothing (live test EX-04). */
export function countryChoices(t: Tr, country: Country | null | undefined): { value: Country | ""; label: string }[] {
  const all = COUNTRIES.map((c) => ({ value: c.value, label: t(c.label) }));
  return country ? all : [{ value: "", label: t("permit.equipmentStep.countryChoose") }, ...all];
}

/** Problems first, then the passed checks, then notes. */
const ORDER: Record<CheckStatus, number> = { problem: 0, pass: 1, info: 2 };

export const STATUS_LABEL: Record<CheckStatus, MessageKey> = {
  problem: "permit.checkStatus.problem",
  pass: "permit.checkStatus.pass",
  info: "permit.checkStatus.info",
};

export const STATUS_TONE: Record<CheckStatus, "danger" | "ok" | undefined> = {
  problem: "danger",
  pass: "ok",
  info: undefined,
};

export function sortChecks<T extends CheckLike>(checks: T[]): T[] {
  return checks
    .map((c, i) => [c, i] as const)
    .sort((a, b) => ORDER[a[0].status] - ORDER[b[0].status] || a[1] - b[1])
    .map(([c]) => c);
}

export function countChecks(checks: CheckLike[]): Record<CheckStatus, number> {
  const out: Record<CheckStatus, number> = { problem: 0, pass: 0, info: 0 };
  for (const c of checks) out[c.status] += 1;
  return out;
}

/** What the utility field is called in each country. */
export function utilityLabel(country: Country | null | undefined): MessageKey {
  return country === "GB" ? "permit.utility.gb" : country === "AU" ? "permit.utility.au" : country === "MX" ? "permit.utility.mx" : "permit.utility.other";
}

export function ahjLabel(country: Country | null | undefined): MessageKey {
  return country === "GB" ? "permit.ahjLabel.gb" : country === "AU" ? "permit.ahjLabel.au" : country === "MX" ? "permit.ahjLabel.mx" : country === "CA" ? "permit.ahjLabel.ca" : "permit.ahjLabel.us";
}

/** Whether the service panel inputs apply (the 120 % busbar check runs in the US, Canada and Mexico). */
export function usesBusbarCheck(country: Country | null | undefined): boolean {
  return country === "US" || country === "CA" || country === "MX" || country == null;
}

export function generateLabel(t: Tr, hundredths: number): string {
  return t("permit.generate.withCost", { lux: hundredths / 100 });
}

/** Why the Generate button is off, or null when it may be pressed (problems never block it). */
export function blockedReason(o: { enabled: boolean; canEdit: boolean; dirty: boolean }): MessageKey | null {
  if (!o.enabled) return "permit.generate.off";
  if (!o.canEdit) return "permit.generate.viewOnly";
  if (o.dirty) return "permit.generate.saveFirst";
  return null;
}

export function problemsText(t: Tr, n: number): string {
  return t("permit.problems", { count: n });
}

/** Inputs with empty strings and NaN dropped, so the preview's cache key and the request agree. */
export function cleanInputs<T extends Record<string, unknown>>(inputs: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(inputs)) {
    if (v === "" || v == null || (typeof v === "number" && Number.isNaN(v))) continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}
